import {
  Grid,
  GridItem,
  Input,
  FormLabel,
  Switch,
  Stack,
  Checkbox,
  InputGroup,
  Box,
  InputLeftAddon,
  SimpleGrid,
  InputRightAddon
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import React, { useEffect, useState } from 'react'

import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { LoadingBlock } from '../../../../components/Loading'
import { MoneyInput } from '../../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../../components/Select'
import { TabErrorLabel } from '../../../../components/TabErrorLabel'
import { formatCurrencyValue, formatNumber, useAppToast } from '../../../../helpers'
import { countErrorsPerTab, firstTabWithErrors, ITabDef } from '../../../../helpers/tabErrorUtils'
import { ICoursesInPerson } from '../../../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { CoursesService } from '../../../../services/CoursesService/CoursesService'
import { ICoursePageFields } from '../../../../services/CoursesService/interfaces'
import { FaqEditor, validateFaqs } from '../../Events/FaqEditor'
import { CourseModulesEditor, validateModules } from './CourseModulesEditor'
import { CoursePageTab } from './CoursePageTab'

/** Preço cheio menos o desconto em %, que é o que a linha "Preço final" mostra. */
const discountedPrice = (fullPrice: number | null, discountPercentage: string): number => {
  const discount = Number(discountPercentage.replace(/[^\d,]/g, '').replace(',', '.')) || 0
  return (fullPrice ?? 0) * (1 - discount / 100)
}

/** A API responde preço como string decimal do banco (`'1234.50'`); o formulário guarda o número. */
const toPrice = (value?: string | null): number | null => {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

export interface IFormData extends ICoursePageFields {
  name: string
  thumbnail: string | File | null
  course_link: string
  taxa_matricula_in_person: number | null
  full_price_in_person: number | null
  discount_percentage_in_person: string
  installments_in_person: string
  taxa_matricula_live_online: number | null
  full_price_live_online: number | null
  discount_percentage_live_online: string
  duration_in_hours: string | number
  duration_in_months: string | number
  promotional_bar: string
  cronometer_type?: string
  cronometer_end_date: string
  cronometer_time: string
  in_person: boolean
  live_online: boolean
  is_technical: boolean
  cta_text_in_person: string
  cta_text_live_online: string
  active_campaign_lists: string[]
  active_campaign_in_person_lists: string[]
  active_campaign_live_online_lists: string[]
  dynamic_ids: string[]
  objective_ids: string[]
  knowledge_area_ids: string[]
}

const emptyForm = (): IFormData => ({
  name: '',
  thumbnail: null,
  course_link: '',
  taxa_matricula_in_person: null,
  full_price_in_person: null,
  discount_percentage_in_person: '0',
  installments_in_person: '1',
  taxa_matricula_live_online: null,
  full_price_live_online: null,
  discount_percentage_live_online: '0',
  duration_in_hours: '0',
  duration_in_months: '0',
  promotional_bar: '0',
  cronometer_type: '0',
  cronometer_end_date: '0',
  cronometer_time: '0',
  in_person: false,
  live_online: false,
  is_technical: false,
  cta_text_in_person: '',
  cta_text_live_online: '',
  active_campaign_lists: [],
  active_campaign_in_person_lists: [],
  active_campaign_live_online_lists: [],
  dynamic_ids: [],
  objective_ids: [],
  knowledge_area_ids: [],
  hero_description: '',
  hero_video_url: '',
  about: '',
  prerequisites: '',
  age_type: '',
  age_value: '',
  modules: [],
  faqs: []
})

/**
 * Os campos de cada aba, só para levar o erro da API até a aba que o mostra. As três últimas
 * são a página do curso presencial no site (ABRA-406) e só aparecem com "Presencial" marcado.
 */
const COURSE_TAB_DEFS: ITabDef[] = [
  {
    key: 'general',
    label: 'Geral',
    fields: [
      'name',
      'thumbnail',
      'course_link',
      'knowledge_area_ids',
      'dynamic_ids',
      'objective_ids',
      'active_campaign_in_person_lists',
      'active_campaign_live_online_lists',
      'format_selection',
      'in_person',
      'live_online',
      'taxa_matricula_in_person',
      'full_price_in_person',
      'discount_percentage_in_person',
      'installments_in_person',
      'cta_text_in_person',
      'taxa_matricula_live_online',
      'full_price_live_online',
      'discount_percentage_live_online',
      'cta_text_live_online',
      'duration_in_hours',
      'duration_in_months',
      'is_technical',
      'promotional_bar',
      'cronometer_type',
      'cronometer_end_date',
      'cronometer_time'
    ]
  },
  {
    key: 'page',
    label: 'Página do curso',
    fields: ['hero_description', 'hero_video_url', 'about', 'prerequisites', 'age_type', 'age_value']
  },
  { key: 'modules', label: 'Módulos', fields: ['modules'] },
  { key: 'faqs', label: 'Perguntas frequentes', fields: ['faqs'] }
]

const PAGE_TAB_KEYS = new Set(['page', 'modules', 'faqs'])

const CoursesModal = ({
  isOpen,
  onClose,
  editCourse,
  onSuccess
}: {
  isOpen: boolean
  onClose: () => void
  editCourse?: Partial<ICoursesInPerson>
  onSuccess?: () => void
}) => {
  const toast = useAppToast()
  const service = new CoursesService()

  const [knowledgeAreas, setKnowledgeAreas] = useState<{ id: number; name: string }[]>([])
  const [dynamics, setDynamics] = useState<{ id: number; name: string }[]>([])
  const [objectives, setObjectives] = useState<{ id: number; name: string }[]>([])
  const [activeCampaignLists, setActiveCampaignLists] = useState<{ id: string; name: string }[]>([])
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)

  useEffect(() => {
    service.getKnowledgeAreas().then(setKnowledgeAreas)
    service.getDynamics().then(setDynamics)
    service.getActiveCampaignLists().then(setActiveCampaignLists)
    service.getObjectives().then(setObjectives)
  }, [])

  const [formData, setFormData] = useState<IFormData>({
    name: editCourse?.name || '',
    thumbnail: editCourse?.thumbnail || null,
    course_link: editCourse?.course_link || '',
    taxa_matricula_in_person: toPrice(editCourse?.price_in_person?.taxa_matricula),
    full_price_in_person: toPrice(editCourse?.price_in_person?.price_original),
    discount_percentage_in_person: editCourse?.price_in_person?.discount || '0',
    installments_in_person: editCourse?.price_in_person?.installments?.toString() || '1',
    taxa_matricula_live_online: toPrice(editCourse?.price_live_online?.taxa_matricula),
    full_price_live_online: toPrice(editCourse?.price_live_online?.price_original),
    discount_percentage_live_online: editCourse?.price_live_online?.discount || '0',
    duration_in_hours: editCourse?.duration_in_hours || '0',
    duration_in_months: editCourse?.duration_in_months || '0',
    promotional_bar: editCourse?.promotional_bar || '0',
    cronometer_type: editCourse?.cronometer_type || '0',
    cronometer_end_date: editCourse?.cronometer_end_date || '0',
    cronometer_time: editCourse?.cronometer_time || '0',
    in_person: editCourse?.in_person || false,
    live_online: editCourse?.live_online || false,
    is_technical: editCourse?.is_technical || false,
    cta_text_in_person: editCourse?.cta_text_in_person || '',
    cta_text_live_online: editCourse?.cta_text_live_online || '',
    active_campaign_lists: editCourse?.active_campaign_lists || [],
    active_campaign_in_person_lists: editCourse?.active_campaign_in_person_lists || [],
    active_campaign_live_online_lists: editCourse?.active_campaign_live_online_lists || [],
    dynamic_ids: editCourse?.dynamic_ids || [],
    objective_ids: editCourse?.objective_ids || [],
    knowledge_area_ids: editCourse?.knowledge_area_ids || [],
    hero_description: '',
    hero_video_url: '',
    about: '',
    prerequisites: '',
    age_type: '',
    age_value: '',
    modules: [],
    faqs: []
  })

  useEffect(() => {
    // Salvar fecha o modal sem passar pelo `clearForm`: o próximo curso abre na aba Geral.
    setTabIndex(0)
    setErrors({})
    if (editCourse?.id) {
      loadCourse(editCourse.id)
    } else {
      setFormData(emptyForm())
    }
  }, [editCourse])

  const loadCourse = async (id: number) => {
    setLoading(true)
    // `finally`, e não um `setLoading(false)` depois do await: uma falha em `getCourse` deixava
    // o modal girando para sempre, sem formulário e sem mensagem.
    let course
    try {
      course = await service.getCourse(id)
    } finally {
      setLoading(false)
    }

    // Definir preview da thumbnail se existir
    // (handled by ImageCropInput via formData.thumbnail)

    setFormData({
      name: course.name || '',
      thumbnail: course.thumbnail || null,
      course_link: course.course_link || '',
      taxa_matricula_in_person: toPrice(course.price_in_person?.taxa_matricula),
      full_price_in_person: toPrice(course.price_in_person?.price_original),
      discount_percentage_in_person: formatNumber(course.price_in_person?.discount?.replace('.', ',') || '0') || '0',
      installments_in_person: course.price_in_person?.installments?.toString() || '1',
      taxa_matricula_live_online: toPrice(course.price_live_online?.taxa_matricula),
      full_price_live_online: toPrice(course.price_live_online?.price_original),
      discount_percentage_live_online:
        formatNumber(course.price_live_online?.discount?.replace('.', ',') || '0') || '0',
      duration_in_hours: course.duration_in_hours || '0',
      duration_in_months: course.duration_in_months || '0',
      promotional_bar: course.promotional_bar || '0',
      cronometer_type: course.cronometer_type || '0',
      cronometer_end_date: course.cronometer_end_date || '0',
      cronometer_time: course.cronometer_time || '0',
      in_person: course.in_person || false,
      live_online: course.live_online || false,
      is_technical: course.is_technical || false,
      cta_text_in_person: course.cta_text_in_person || '',
      cta_text_live_online: course.cta_text_live_online || '',
      active_campaign_lists: [],
      active_campaign_in_person_lists: course.active_campaign_in_person_lists || [],
      active_campaign_live_online_lists: course.active_campaign_live_online_lists || [],
      dynamic_ids: course.dynamics.map((d) => d.id.toString()) || [],
      objective_ids: course.objectives.map((o) => o.id.toString()) || [],
      knowledge_area_ids: course.knowledge_areas.map((k) => k.id.toString()) || [],
      hero_description: course.hero_description || '',
      hero_video_url: course.hero_video_url || '',
      about: course.about || '',
      prerequisites: course.prerequisites || '',
      age_type: course.age_type || '',
      age_value: course.age_value || '',
      modules: (course.modules || []).map((m) => ({
        _id: crypto.randomUUID(),
        id: m.id,
        title: m.title,
        content: m.content || ''
      })),
      faqs: (course.faqs || []).map((f) => ({
        _id: crypto.randomUUID(),
        id: f.id,
        question: f.question,
        answer: f.answer
      }))
    })
  }

  // Sem "Presencial" a página do curso não existe no site, e as abas dela saem.
  const visibleTabDefs = formData.in_person
    ? COURSE_TAB_DEFS
    : COURSE_TAB_DEFS.filter((tab) => !PAGE_TAB_KEYS.has(tab.key))
  const errorCounts = countErrorsPerTab(visibleTabDefs, errors)

  const showErrors = (next: Record<string, string[]>) => {
    setErrors(next)
    const offendingTab = firstTabWithErrors(visibleTabDefs, next)
    if (offendingTab >= 0) setTabIndex(offendingTab)
  }

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const listErrors = { ...validateModules(formData.modules), ...validateFaqs(formData.faqs) }
    if (Object.keys(listErrors).length > 0) {
      showErrors(listErrors)
      toast.error('Erro de validação', 'Preencha o título dos módulos e a pergunta e a resposta das FAQs.')
      return
    }

    setSaving(true)
    setErrors({})
    try {
      if (editCourse && editCourse.id !== undefined) {
        await service.update(editCourse.id, formData)
        toast.success('Curso editado com sucesso!')
        onSuccess?.()
      } else {
        await service.create(formData)
        toast.success('Curso criado com sucesso!')
        onSuccess?.()
        setFormData(emptyForm())
      }
      onClose()
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ errors: Record<string, string[]> }>
      toast.error(
        editCourse && editCourse.id !== undefined ? 'Erro ao editar curso' : 'Erro ao criar curso',
        'Verifique os dados e tente novamente.'
      )
      if (axiosError.response?.data?.errors) {
        showErrors(axiosError.response.data.errors)
      }
    } finally {
      setSaving(false)
    }
  }

  const clearForm = () => {
    setFormData(emptyForm())
    setErrors({})
    setTabIndex(0)
    onClose()
  }

  const generalTab = (
    <Grid templateColumns="repeat(2, 1fr)" gap={6}>
      {/* name */}
      <GridItem>
        <FormField label="Nome" htmlFor="name" error={errors.name}>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                name: e.target.value
              }))
            }
            type="text"
          />
        </FormField>
      </GridItem>

      {/* thumbnail */}
      <GridItem rowSpan={4}>
        <ImageCropInput
          label="Thumbnail"
          value={formData.thumbnail}
          onChange={(file) => setFormData((prev) => ({ ...prev, thumbnail: file }))}
          aspectRatio={450 / 210}
          suggestedSize="450px x 210px"
          previewAspectRatio="450 / 210"
          error={errors.thumbnail}
        />
      </GridItem>

      {/* course link */}
      <GridItem>
        <FormField label="Link do curso" htmlFor="course_link" error={errors.course_link}>
          <Input
            id="course_link"
            name="course_link"
            value={formData.course_link}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                course_link: e.target.value
              }))
            }
          />
        </FormField>
      </GridItem>

      {/* knowledge area */}
      <GridItem>
        <FormField label="Área de conhecimento" error={errors.knowledge_area_ids}>
          <Box>
            <SearchableSelect
              isMulti
              value={formData.knowledge_area_ids}
              options={knowledgeAreas.map((area) => ({ label: area.name, value: String(area.id) }))}
              onChange={(values) => setFormData({ ...formData, knowledge_area_ids: values })}
              placeholder="Selecione uma ou mais áreas"
              entity="área"
              feminine
            />
          </Box>
        </FormField>
      </GridItem>

      {/* dynamics */}
      <GridItem>
        <FormField label="Dinâmica de Ensino" error={errors.dynamic_ids}>
          <SearchableSelect
            isMulti
            value={formData.dynamic_ids}
            options={dynamics.map((dynamic) => ({ label: dynamic.name, value: String(dynamic.id) }))}
            onChange={(values) => setFormData({ ...formData, dynamic_ids: values })}
            placeholder="Selecione uma ou mais dinâmicas"
            entity="dinâmica"
            feminine
          />
        </FormField>
      </GridItem>

      {/* campaing in person */}
      <GridItem>
        <Box>
          <FormField label="Listas AC Presencial" error={errors.active_campaign_in_person_lists}>
            <SearchableSelect
              isMulti
              value={formData.active_campaign_in_person_lists}
              options={activeCampaignLists.map((campaign) => ({
                label: campaign.name,
                value: String(campaign.id)
              }))}
              onChange={(values) => setFormData({ ...formData, active_campaign_in_person_lists: values })}
              placeholder="Selecione uma ou mais listas"
              entity="lista"
              feminine
            />
          </FormField>
        </Box>
      </GridItem>

      {/* campaign online */}
      <GridItem>
        <Box>
          <FormField label="Listas AC Online Ao Vivo" error={errors.active_campaign_live_online_lists}>
            <SearchableSelect
              isMulti
              value={formData.active_campaign_live_online_lists}
              options={activeCampaignLists.map((campaign) => ({
                label: campaign.name,
                value: String(campaign.id)
              }))}
              onChange={(values) => setFormData({ ...formData, active_campaign_live_online_lists: values })}
              placeholder="Selecione uma ou mais listas"
              entity="lista"
              feminine
            />
          </FormField>
        </Box>
      </GridItem>

      {/* objectives */}
      <GridItem colSpan={2}>
        <FormField label="Objetivos do Curso" error={errors.objective_ids}>
          <SearchableSelect
            isMulti
            value={formData.objective_ids}
            options={objectives.map((objective) => ({ label: objective.name, value: String(objective.id) }))}
            onChange={(values) => setFormData({ ...formData, objective_ids: values })}
            placeholder="Selecione um ou mais objetivos"
            entity="objetivo"
          />
        </FormField>
      </GridItem>

      {/* formato de ensino */}
      <GridItem colSpan={2}>
        <FormField label="Formato de ensino" error={errors.in_person || errors.live_online}>
          <Stack spacing={8}>
            <Box display={'flex'} flexDirection={'column'} justifyContent="space-between" gap={2}>
              <Checkbox
                isChecked={formData.in_person}
                onChange={(e) => setFormData((prev) => ({ ...prev, in_person: e.target.checked }))}
              >
                Presencial
              </Checkbox>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                <GridItem>
                  <FormField
                    label="Taxa de Matrícula"
                    htmlFor="taxa_matricula_in_person"
                    error={errors.taxa_matricula_in_person}
                  >
                    <MoneyInput
                      id="taxa_matricula_in_person"
                      name="taxa_matricula_in_person"
                      value={formData.taxa_matricula_in_person}
                      isDisabled={!formData.in_person}
                      onChange={(taxa_matricula_in_person) =>
                        setFormData((prev) => ({ ...prev, taxa_matricula_in_person }))
                      }
                    />
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Preço cheio" htmlFor="full_price_in_person" error={errors.full_price_in_person}>
                    <MoneyInput
                      id="full_price_in_person"
                      name="full_price_in_person"
                      value={formData.full_price_in_person}
                      isDisabled={!formData.in_person}
                      onChange={(full_price_in_person) => setFormData((prev) => ({ ...prev, full_price_in_person }))}
                    />
                  </FormField>
                </GridItem>
                {/* discount */}
                <GridItem>
                  <FormLabel htmlFor="discount_percentage_in_person">Desconto (%)</FormLabel>
                  <InputGroup>
                    <Input
                      id="discount_percentage_in_person"
                      name="discount_percentage_in_person"
                      placeholder="0"
                      type="text"
                      value={formData.discount_percentage_in_person}
                      disabled={!formData.in_person}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          discount_percentage_in_person: formatNumber(e.target.value)
                        }))
                      }
                    />
                    <InputRightAddon>%</InputRightAddon>
                  </InputGroup>
                </GridItem>
                <GridItem>
                  <FormField
                    label="Qtd. de parcelas"
                    htmlFor="installments_in_person"
                    error={errors.installments_in_person}
                  >
                    <Input
                      id="installments_in_person"
                      name="installments_in_person"
                      placeholder="1"
                      type="number"
                      min={1}
                      max={30}
                      value={formData.installments_in_person}
                      disabled={!formData.in_person}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          installments_in_person: e.target.value
                        }))
                      }
                    />
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormLabel htmlFor="discounted_price_in_person">Preço final</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>R$</InputLeftAddon>
                    <Input
                      id="discounted_price_in_person"
                      name="discounted_price_in_person"
                      value={formatCurrencyValue(
                        discountedPrice(formData.full_price_in_person, formData.discount_percentage_in_person)
                      )}
                      disabled={!formData.in_person}
                      readOnly
                    />
                  </InputGroup>
                </GridItem>
              </SimpleGrid>
              <SimpleGrid columns={1} spacing={4} mt={4}>
                <GridItem>
                  <FormField label="Texto CTA" htmlFor="cta_text_in_person" error={errors.cta_text_in_person}>
                    <Input
                      id="cta_text_in_person"
                      name="cta_text_in_person"
                      placeholder="Ex: Matricule-se agora!"
                      type="text"
                      maxLength={255}
                      value={formData.cta_text_in_person}
                      disabled={!formData.in_person}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          cta_text_in_person: e.target.value
                        }))
                      }
                    />
                  </FormField>
                </GridItem>
              </SimpleGrid>
            </Box>
            <Box display={'flex'} flexDirection={'column'} justifyContent="space-between" gap={2}>
              <Checkbox
                isChecked={formData.live_online}
                onChange={(e) => setFormData((prev) => ({ ...prev, live_online: e.target.checked }))}
              >
                Ao Vivo
              </Checkbox>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                <GridItem>
                  <FormField
                    label="Taxa de Matrícula"
                    htmlFor="taxa_matricula_live_online"
                    error={errors.taxa_matricula_live_online}
                  >
                    <MoneyInput
                      id="taxa_matricula_live_online"
                      name="taxa_matricula_live_online"
                      value={formData.taxa_matricula_live_online}
                      isDisabled={!formData.live_online}
                      onChange={(taxa_matricula_live_online) =>
                        setFormData((prev) => ({ ...prev, taxa_matricula_live_online }))
                      }
                    />
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Preço cheio" htmlFor="full_price_live_online" error={errors.full_price_live_online}>
                    <MoneyInput
                      id="full_price_live_online"
                      name="full_price_live_online"
                      value={formData.full_price_live_online}
                      isDisabled={!formData.live_online}
                      onChange={(full_price_live_online) =>
                        setFormData((prev) => ({ ...prev, full_price_live_online }))
                      }
                    />
                  </FormField>
                </GridItem>
                {/* discount */}
                <GridItem>
                  <FormLabel htmlFor="discount_percentage_live_online">Desconto (%)</FormLabel>
                  <InputGroup>
                    <Input
                      id="discount_percentage_live_online"
                      name="discount_percentage_live_online"
                      placeholder="0"
                      type="text"
                      value={formData.discount_percentage_live_online}
                      disabled={!formData.live_online}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          discount_percentage_live_online: formatNumber(e.target.value)
                        }))
                      }
                    />
                    <InputRightAddon>%</InputRightAddon>
                  </InputGroup>
                </GridItem>
                <GridItem>
                  <FormLabel htmlFor="discounted_price_live_online">Preço final</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>R$</InputLeftAddon>
                    <Input
                      id="discounted_price_live_online"
                      name="discounted_price_live_online"
                      value={formatCurrencyValue(
                        discountedPrice(formData.full_price_live_online, formData.discount_percentage_live_online)
                      )}
                      disabled={!formData.live_online}
                      readOnly
                    />
                  </InputGroup>
                </GridItem>
              </SimpleGrid>
              <SimpleGrid columns={1} spacing={4} mt={4}>
                <GridItem>
                  <FormField label="Texto CTA" htmlFor="cta_text_live_online" error={errors.cta_text_live_online}>
                    <Input
                      id="cta_text_live_online"
                      name="cta_text_live_online"
                      placeholder="Ex: Matricule-se agora!"
                      type="text"
                      maxLength={255}
                      value={formData.cta_text_live_online}
                      disabled={!formData.live_online}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          cta_text_live_online: e.target.value
                        }))
                      }
                    />
                  </FormField>
                </GridItem>
              </SimpleGrid>
            </Box>
          </Stack>
        </FormField>
      </GridItem>

      {/* duration (hours) */}
      <GridItem>
        <FormField label="Duração (horas)" htmlFor="duration_in_hours" error={errors.duration_in_hours}>
          <Input
            id="duration_in_hours"
            name="duration_in_hours"
            value={formData.duration_in_hours}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                duration_in_hours: e.target.value
              }))
            }
            type="number"
          />
        </FormField>
      </GridItem>

      {/* duration (months) */}
      <GridItem>
        <FormField label="Duração (meses)" htmlFor="duration_in_months" error={errors.duration_in_months}>
          <Input
            id="duration_in_months"
            name="duration_in_months"
            value={formData.duration_in_months}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                duration_in_months: e.target.value
              }))
            }
            type="number"
          />
        </FormField>
      </GridItem>

      {/* Is technical */}
      <GridItem colSpan={2}>
        <FormField label="Técnico" htmlFor="is_technical" error={errors.is_technical}>
          <Box
            as="label"
            htmlFor="is_technical"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            px={3}
            py={2}
            display="inline-block"
            cursor="pointer"
          >
            <Switch
              id="is_technical"
              name="is_technical"
              isChecked={formData.is_technical}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_technical: e.target.checked
                }))
              }
            />
          </Box>
        </FormField>
      </GridItem>

      {/* promotional bar */}
      <GridItem colSpan={2}>
        <FormField label="Habilitar Barra de Promoção" htmlFor="promotional_bar" error={errors.promotional_bar}>
          <Box
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            px={3}
            py={2}
            display="inline-block"
            cursor="pointer"
          >
            <Switch
              id="promotional_bar"
              name="promotional_bar"
              isChecked={formData.promotional_bar === '1'}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  promotional_bar: e.target.checked ? '1' : '0',
                  cronometer_type: 'disabled',
                  cronometer_end_date: '',
                  cronometer_time: ''
                }))
              }
            />
          </Box>
        </FormField>
      </GridItem>

      {formData.promotional_bar === '1' && (
        <>
          <GridItem colSpan={2}>
            <FormField label="Cronômetro" htmlFor="cronometer_enabled">
              <Box
                border="1px solid"
                borderColor="gray.300"
                borderRadius="md"
                px={3}
                py={2}
                display="inline-block"
                cursor="pointer"
              >
                <Switch
                  id="cronometer_enabled"
                  name="cronometer_enabled"
                  isChecked={formData.cronometer_type !== 'disabled'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      cronometer_type: e.target.checked ? '' : 'disabled',
                      cronometer_end_date: '',
                      cronometer_time: ''
                    }))
                  }
                />
              </Box>
            </FormField>
          </GridItem>

          {formData.cronometer_type !== 'disabled' && (
            <>
              <GridItem colSpan={1}>
                <FormField label="Tipo de Cronômetro" htmlFor="cronometer_type" error={errors.cronometer_type}>
                  <FormSelect
                    id="cronometer_type"
                    name="cronometer_type"
                    value={formData.cronometer_type ?? ''}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        cronometer_type: value,
                        cronometer_end_date: '',
                        cronometer_time: ''
                      }))
                    }
                    options={[
                      { value: 'end_date', label: 'Data de término' },
                      { value: 'time', label: 'Renovação automática' }
                    ]}
                    placeholder="Selecione o tipo"
                  />
                </FormField>
              </GridItem>

              {formData.cronometer_type === 'end_date' && (
                <GridItem colSpan={1}>
                  <FormField label="Data de término" htmlFor="cronometer_end_date" error={errors.cronometer_end_date}>
                    <Input
                      id="cronometer_end_date"
                      name="cronometer_end_date"
                      type="datetime-local"
                      value={
                        formData.cronometer_end_date
                          ? new Date(formData.cronometer_end_date).toISOString().slice(0, 16)
                          : ''
                      }
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          cronometer_end_date: e.target.value
                        }))
                      }
                    />
                  </FormField>
                </GridItem>
              )}

              {formData.cronometer_type === 'time' && (
                <GridItem colSpan={1}>
                  <FormField
                    label="Renovação automática (dias)"
                    htmlFor="cronometer_time"
                    error={errors.cronometer_time}
                  >
                    <Input
                      id="cronometer_time"
                      name="cronometer_time"
                      type="number"
                      value={formData.cronometer_time || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          cronometer_time: e.target.value
                        }))
                      }
                    />
                  </FormField>
                </GridItem>
              )}
            </>
          )}
        </>
      )}
    </Grid>
  )

  const tabLabel = (index: number) => <TabErrorLabel label={visibleTabDefs[index]?.label} count={errorCounts[index]} />

  return (
    <FormModal
      isOpen={isOpen}
      // O `clearForm` estava só no botão Fechar; aqui vale também para o X e o clique fora,
      // que antes deixavam o formulário preenchido para a próxima abertura.
      onClose={() => {
        clearForm()
        onClose()
      }}
      title={`${editCourse ? 'Editar ' : 'Adicionar novo '}curso`}
      onSubmit={handleSave}
      size="editor"
      cancelLabel="Fechar"
      isLoading={saving}
      isConfirmDisabled={loading}
      // Um erro numa aba fechada travava o Salvar em silêncio: a API valida e leva o erro à aba.
      noValidate
      tabIndex={Math.min(tabIndex, visibleTabDefs.length - 1)}
      onTabChange={setTabIndex}
      tabs={
        loading
          ? undefined
          : [
              { label: tabLabel(0), content: generalTab },
              ...(formData.in_person
                ? [
                    {
                      label: tabLabel(1),
                      content: (
                        <CoursePageTab
                          formData={formData}
                          setFormData={setFormData}
                          errors={errors}
                          heroDescriptionHelp="Vazia, o site mostra: Aprenda {nome do curso} na ABRA, em Moema: aulas presenciais com professores atuantes no mercado e certificado ao concluir."
                        />
                      )
                    },
                    {
                      label: tabLabel(2),
                      content: (
                        <CourseModulesEditor
                          modules={formData.modules}
                          onChange={(modules) => setFormData((prev) => ({ ...prev, modules }))}
                          disabled={saving}
                          fieldErrors={errors}
                        />
                      )
                    },
                    {
                      label: tabLabel(3),
                      content: (
                        <FaqEditor
                          faqs={formData.faqs}
                          onChange={(faqs) => setFormData((prev) => ({ ...prev, faqs }))}
                          disabled={saving}
                          fieldErrors={errors}
                        />
                      )
                    }
                  ]
                : [])
            ]
      }
    >
      {loading && <LoadingBlock />}
    </FormModal>
  )
}

export default CoursesModal
